import {showNotification} from "@mantine/notifications";
import {
    AiOutlineExclamation,
    AiOutlineInfo,
    AiOutlineLogin,
    AiOutlineWarning,
    AiOutlineCheckCircle
} from "react-icons/ai";
import config from "../config";


// FNP-960: a caught value is `unknown`, not `any`. Typing it honestly showed
// this read `.errors` off the value with no narrowing — an unchecked property
// access that would throw on a non-object rejection.
const hasErrorsArray = (e: unknown): e is { errors: { message: string }[] } =>
    typeof e === 'object'
    && e !== null
    && Array.isArray((e as { errors?: unknown }).errors);

export const showErrorNotifications = (e: unknown) => {

    console.trace(e);

    console.debug('handleAppSyncResponseException', e)

    if (hasErrorsArray(e) && e.errors.length > 0) {
        e.errors.forEach((error: { message: string; }) => {
            showErrorNotification(error.message);
        })
    } else {
        // FNP-960: a thrown string or non-Error previously produced undefined
        // here; it now falls through to a stable message.
        showErrorNotification(
            e instanceof Error && e.message ? e.message : 'Error performing the operation'
        );
    }
}

// Enhanced error notification with specific error types
export const showValidationErrorNotification = (message: string, details?: string[]) => {
    showNotification({
        title: 'Validation Error',
        icon: <AiOutlineWarning/>,
        message: details ? `${message}\n${details.join('\n')}` : message,
        color: 'orange',
        autoClose: 8000,
    });
}

// Network/GraphQL error notification
export const showNetworkErrorNotification = (message: string = 'Network error occurred. Please check your connection and try again.') => {
    showNotification({
        title: 'Connection Error',
        icon: <AiOutlineExclamation/>,
        message: message,
        color: 'red',
        autoClose: 10000,
    });
}

// File processing error notification
export const showFileProcessingErrorNotification = (message: string, fileName?: string) => {
    showNotification({
        title: 'File Processing Error',
        icon: <AiOutlineWarning/>,
        message: fileName ? `${message} (File: ${fileName})` : message,
        color: 'orange',
        autoClose: 8000,
    });
}

// Import progress notification
export const showImportProgressNotification = (successful: number, failed: number, skipped: number) => {
    const total = successful + failed + skipped;
    let message = `Import completed: ${successful}/${total} successful`;
    
    if (failed > 0) {
        message += `, ${failed} failed`;
    }
    if (skipped > 0) {
        message += `, ${skipped} skipped`;
    }

    const color = failed === 0 ? 'green' : failed < successful ? 'orange' : 'red';
    const icon = failed === 0 ? <AiOutlineCheckCircle/> : <AiOutlineWarning/>;

    showNotification({
        title: 'Import Results',
        icon: icon,
        message: message,
        color: color,
        autoClose: 10000,
    });
}

// Partial import failure notification
export const showPartialImportFailureNotification = (successful: number, failed: number, totalErrors: number) => {
    showNotification({
        title: 'Partial Import Failure',
        icon: <AiOutlineWarning/>,
        message: `${successful} domains imported successfully, but ${failed} failed. ${totalErrors} errors occurred. Check the error report for details.`,
        color: 'orange',
        autoClose: 12000,
    });
}


export const showSuccessNotification = (message = 'Record saved successfully') => {
    showNotification({
                         title: 'Success',
                         icon: <AiOutlineInfo/>,
                         message: message,
                         color: 'teal',
                     })
}

export const showErrorNotification = (message = 'Error performing the operation') => {

    showNotification({
                         title: 'Error',
                         icon: <AiOutlineExclamation/>,
                         message: message,
                         color: 'red',
                     })

    const sessionExpiredErrors = ['no current user', 'no federated jwt']
    if (sessionExpiredErrors.includes(message.toLowerCase())) {
        showSessionExpiredNotification();
    }

}

export const showSessionExpiredNotification = (message = 'Redirecting to network portal...Please, wait!') => {

    showNotification({
                         title: 'Session Expired',
                         icon: <AiOutlineLogin/>,
                         message: message,
                         color: 'blue',
                     });

    setTimeout(() => {
        window.location.assign(config.platform.portal + '/sign-in?returnTo=' +
                                   config.platform.domains);
    }, 3000);

};


// notify access denied
export const notifyAccessDenied = (message = 'Access denied') => {
    showNotification({
                         title: 'Access Denied',
                         icon: <AiOutlineExclamation/>,
                         message: message,
                         color: 'red',
                     })
}
